import React from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from '@/contexts/AuthContext';
import logoImg from "@/assets/logo.png";

import {
  confirmResidentPasswordReset,
  requestResidentPasswordReset,
  verifyResidentPasswordReset,
} from "@/api/residentAuth";

const PRIMARY = "#1F3A93";
const BG = "#F5F6FA";

type Step = "request" | "verify" | "confirm";

function validatePassword(pwd: string) {
  return {
    minLength: pwd.length >= 8,
    hasLetter: /[a-zA-Z]/.test(pwd),
    hasNumber: /\d/.test(pwd),
  };
}

export default function ResidentPasswordResetPage() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [step, setStep] = React.useState<Step>("request");
  const [email, setEmail] = React.useState("");
  const [code, setCode] = React.useState("");
  const [resetToken, setResetToken] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = React.useState(0);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const pwdValidation = validatePassword(newPassword);
  const pwdValid = pwdValidation.minLength && pwdValidation.hasLetter && pwdValidation.hasNumber;
  const pwdMatch = newPassword === confirmPassword;

  async function handleRequest(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response = await requestResidentPasswordReset({ email: email.trim() });
      setMessage(response.message);
      setStep("verify");
      setResendCooldown(300);
    } catch (err: any) {
      setError(err?.response?.data?.message || t("auth.unableToRequestReset"));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response = await verifyResidentPasswordReset({ email: email.trim(), code });
      setResetToken(response.reset_token);
      setMessage(response.message);
      setStep("confirm");
    } catch (err: any) {
      setError(err?.response?.data?.message || t("auth.unableToVerify"));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleResendCode() {
    if (resendCooldown > 0 || isLoading) return;
    setIsLoading(true);
    setError(null);
    setMessage(null);
    try {
      await requestResidentPasswordReset({ email: email.trim() });
      setResendCooldown(300);
      setMessage(t("auth.codeSent"));
    } catch (err: any) {
      setError(err?.response?.data?.message || t("auth.unableToRequestReset"));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!pwdValid) {
      setError(t("auth.passwordInvalid"));
      return;
    }
    if (!pwdMatch) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    setIsLoading(true);
    setError(null);
    setMessage(null);
    try {
      const response = await confirmResidentPasswordReset({
        reset_token: resetToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setMessage(response.message);
      // Reset revokes the old credentials. Clear the browser's cached user as
      // well, otherwise /login redirects straight back into the stale session.
      await logout();
      setTimeout(() => navigate("/login"), 1500);
    } catch (err: any) {
      setError(err?.response?.data?.message || t("auth.unableToReset"));
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: BG }}>
      <div className="w-full max-w-[480px] rounded-3xl border border-black/10 bg-white shadow-md p-6 md:p-8">
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <img src={logoImg} alt="Urbe360 logo" width={80} height={80} className="mb-2" />
          <span className="text-[16px] font-semibold text-gray-900">Urbe360</span>
        </div>

        <h1 className="text-2xl font-semibold text-gray-900 text-center">
          {t("auth.passwordResetTitle")}
        </h1>
        <p className="mt-2 text-sm text-gray-600 text-center">
          {t("auth.passwordResetSubtitle")}
        </p>

        {message && (
          <div className="mt-5 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
            {message}
          </div>
        )}

        {error && (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {step === "request" && (
          <form onSubmit={handleRequest} className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm text-gray-800">
                {t("auth.email")}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isLoading}
                className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 outline-none focus:border-blue-400"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || !email.trim()}
              className="w-full rounded-2xl px-4 py-3 text-white font-semibold disabled:opacity-60"
              style={{ backgroundColor: PRIMARY }}
            >
              {isLoading ? t("auth.sending") : t("auth.sendResetCode")}
            </button>
          </form>
        )}

        {step === "verify" && (
          <form onSubmit={handleVerify} className="mt-6 space-y-4">
            <div>
              <label htmlFor="otp" className="block text-sm text-gray-800">
                {t("auth.verificationCode")}
              </label>
              <input
                id="otp"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                maxLength={6}
                disabled={isLoading}
                className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 text-center text-2xl tracking-widest outline-none focus:border-blue-400"
              />
            </div>
            <button
              type="submit"
              disabled={isLoading || code.length !== 6}
              className="w-full rounded-2xl px-4 py-3 text-white font-semibold disabled:opacity-60"
              style={{ backgroundColor: PRIMARY }}
            >
              {isLoading ? t("auth.verifying") : t("auth.verifyCode")}
            </button>
            <button
              type="button"
              onClick={handleResendCode}
              disabled={resendCooldown > 0 || isLoading}
              className="w-full text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40 py-1"
            >
              {resendCooldown > 0
                ? `${t("resident.otp.resendIn")} ${resendCooldown}s`
                : t("resident.otp.resendCode")}
            </button>
          </form>
        )}

        {step === "confirm" && (
          <form onSubmit={handleConfirm} className="mt-6 space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm text-gray-800">
                {t("auth.newPassword")}
              </label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={isLoading}
                className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 outline-none focus:border-blue-400"
              />
              {newPassword && (
                <ul className="mt-2 space-y-1 text-xs">
                  <li className={pwdValidation.minLength ? "text-green-600" : "text-red-500"}>
                    {pwdValidation.minLength ? "✓" : "✗"} {t("auth.passwordReqLength")}
                  </li>
                  <li className={pwdValidation.hasLetter ? "text-green-600" : "text-red-500"}>
                    {pwdValidation.hasLetter ? "✓" : "✗"} {t("auth.passwordReqLetter")}
                  </li>
                  <li className={pwdValidation.hasNumber ? "text-green-600" : "text-red-500"}>
                    {pwdValidation.hasNumber ? "✓" : "✗"} {t("auth.passwordReqNumber")}
                  </li>
                </ul>
              )}
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm text-gray-800">
                {t("auth.confirmPassword")}
              </label>
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                className="mt-1 w-full rounded-2xl border border-black/10 px-4 py-3 outline-none focus:border-blue-400"
              />
              {confirmPassword && !pwdMatch && (
                <p className="mt-1 text-xs text-red-500">{t("auth.passwordMismatch")}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isLoading || !pwdValid || !pwdMatch || !confirmPassword}
              className="w-full rounded-2xl px-4 py-3 text-white font-semibold disabled:opacity-60"
              style={{ backgroundColor: PRIMARY }}
            >
              {isLoading ? t("auth.resetting") : t("auth.resetPassword")}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => navigate("/login")}
            className="text-sm text-gray-600 hover:text-gray-900 underline underline-offset-4"
          >
            {t("auth.backToLogin")}
          </button>
        </div>
      </div>
    </div>
  );
}
